import logger from "../common/Logger"
import $ from "jquery";
import CoreService from "./CoreService";
import ApiService from "./ApiService";
import ConfigService, {ConfigConstants} from "./ConfigService";
import AuthService from "./AuthService";
import StorageService from "./StorageService";
import {currentSiteName} from "./SiteService";

class SubscriptionService {
    /**
     * @type {Array}
     * @private
     */
    static _productCache = null;

    /**
     * Fetch the subscription status.
     *
     * @param forceFetch whether to force retrieval from the API
     * @param callback the callback to invoke with the outcome
     */
    static fetchSubscriptionStatus(forceFetch, callback) {
        if (typeof forceFetch === 'undefined') {
            forceFetch = false;
        }

        const cachedSubscription = StorageService.getSessionItem('subscriptionStatus');
        if (forceFetch || !cachedSubscription) {
            ApiService.makeRequest('/api/subscription', {
                type: 'GET',
                success(subscription) {
                    logger.info("Fetched subscription with status: " + subscription.status);
                    StorageService.setSessionItem('subscriptionStatus', subscription);
                    if (callback) {
                        callback(subscription);
                    }
                },
                error(result, err) {
                    if (err) logger.error('Error loading subscription details:', err);
                    CoreService.showPassiveError("Unable to load subscription details.");
                    if (callback) {
                        callback(null, err);
                    }
                }
            });

        } else {
            if (callback) {
                callback(cachedSubscription);
            }
        }
    }

    /**
     * Check subscription if status is active. Checks cached copy if present.
     *
     * @param callback the callback to invoke with a boolean result
     */
    static checkSubscriptionActive(callback) {
        SubscriptionService.fetchSubscriptionStatus(false, function (subscription) {
            callback(subscription && subscription.status === 'active');
        });
    }

    /**
     * Invoke the callback only if the subscription status is active. Checks cached copy if present.
     *
     * @param callback the callback to conditionally invoke
     */
    static doIfSubscriptionActive(callback) {
        SubscriptionService.checkSubscriptionActive(function (active) {
            if (active) {
                callback();

            } else {
                CoreService.showModal("Subscription Required", "Oops! You need an active subscription to use this feature.", {
                    onHide() {
                        CoreService.navigateTo("/subscription");
                    }
                });
            }
        });
    }

    /**
     * Fetch product details.
     *
     * @param productId {String}
     * @param callback {Function}
     */
    static fetchProduct(productId, callback) {
        if (null === this._productCache) {
            const that = this;

            $.ajax('/assets/products.json', {
                type: 'GET',
                success: function (content) {
                    logger.debug('Retrieved product configuration');
                    that._productCache = (typeof content === 'object' ? content : JSON.parse(content));
                    that.callBackWithProduct(productId, callback);
                },
                error: function (err) {
                    logger.error('Failed to retrieve product configuration', err);
                }
            });

        } else {
            // cache already populated
            this.callBackWithProduct(productId, callback);
        }
    }

    static callBackWithProduct(productId, callback) {
        const rawProduct = this._productCache.find(value => value.id === productId);

        let product;
        if (rawProduct !== undefined) {
            product = {
                id: rawProduct.id,
                title: rawProduct.name,
                description: rawProduct.description,
                amount: rawProduct.priceInPence
            };

        } else {
            logger.warn(`Falling back to default product for ID: ${productId}`);

            // default fallback product
            product = {
                id: productId,
                title: productId,
                description: 'Subscription',
                amount: 300
            };
        }

        callback(product);
    }

    /**
     * Set up the subscription and payment source.
     *
     * @param product {Object}
     * @param product.id {String} plan ID
     * @param onSetupComplete {Function}
     * @param onSubscriptionSuccess {Function}
     */
    static setUpPayment(product, onSetupComplete, onSubscriptionSuccess) {
        if (typeof window.StripeCheckout === 'undefined') {
            logger.debug("Loading Stripe JS...");

            // Stripe require that their JS be served from their servers
            $.getScript("https://checkout.stripe.com/checkout.js").done(() => {
                logger.debug("Loaded Stripe JS");

                ConfigService.getItem(ConfigConstants.stripeKey, (stripeKey) => {
                    SubscriptionService.configureStripe(stripeKey, product, onSetupComplete, onSubscriptionSuccess)
                });

            }).fail((err) => {
                logger.error('Failed to load Stripe JS', err);
                onSetupComplete(false);
            });
        }
    }

    /**
     * Configure Stripe for the given product.
     *
     * @param stripeKey {String}
     * @param product {Object}
     * @param onSetupComplete {Function}
     * @param onSubscriptionSuccess {Function}
     */
    static configureStripe(stripeKey, product, onSetupComplete, onSubscriptionSuccess) {
        logger.debug('Configuring Stripe');

        const checkoutLogoUrl = `${CoreService.getBasePath()}/assets/img/gatehillsoftware_logo01_black_transparent_256.png`;

        SubscriptionService.handler = window.StripeCheckout.configure({
            key: stripeKey,
            image: checkoutLogoUrl,
            locale: 'auto',
            panelLabel: 'Subscribe',
            email: AuthService.getUserSession().email,
            token: (token) => {
                SubscriptionService.startSubscription(product.id, token.id, token.client_ip, token.created, onSubscriptionSuccess);
            }
        });

        onSetupComplete(true);
    }

    /**
     * Once `setUpPayment` has been called, this will display the payment form.
     *
     * @param product {Object}
     * @param product.id {String} plan ID
     * @param product.description {number} product description
     * @param product.amount {number} product amount
     */
    static openPaymentForm(product) {
        // Open Checkout with further options
        SubscriptionService.handler.open({
            name: currentSiteName,
            description: product.description,
            zipCode: true,
            currency: 'gbp',
            amount: product.amount
        });
    }

    /**
     * Start the subscription for the given token.
     *
     * @param planId {String}
     * @param tokenId {String}
     * @param clientIp {String}
     * @param created {Date}
     * @param callback {Function}
     */
    static startSubscription(planId, tokenId, clientIp, created, callback = null) {
        logger.trace('Submitting subscription request for token');
        CoreService.showLoadingModal('Setting up subscription...');

        const subscriptionRequest = JSON.stringify({
            planId: planId,
            token: {
                id: tokenId,
                clientIp: clientIp,
                created: created
            }
        });

        ApiService.makeRequest('/api/subscription', {
            type: 'POST',
            data: subscriptionRequest,
            success() {
                logger.info("Successfully set up subscription");
                CoreService.hideLoadingModal();
                CoreService.showModal('Subscription', 'Subscription set up successfully.', {
                    onHide: callback
                });
            },
            error(result, err) {
                if (err) logger.error(err);
                CoreService.hideLoadingModal();
                CoreService.showModal('Subscription', 'Error setting up subscription.');
            }
        });
    }

    /**
     * Fetch the default product ID.
     *
     * @param callback {Function}
     */
    static fetchDefaultProductId(callback) {
        ConfigService.getItem(ConfigConstants.defaultProductId, (defaultProductId) => {
            callback(defaultProductId)
        });
    }
}

export default SubscriptionService;



// WEBPACK FOOTER //
// ./src/modules/service/SubscriptionService.js